import { EventDispatcher } from '../events/EventDispatcher';
import { Event } from '../events/Event';
import { Point } from '../geom/Point';
import { Rectangle } from '../geom/Rectangle';
import { Transform } from '../geom/Transform';
import { BitmapFilter } from '../filters/filters';
import { rt } from '../player/runtime';
import {
  IDENTITY,
  TMatrix,
  TRect,
  TWIPS,
  fround,
  invalidRect,
  rectContains,
  rectUnion,
  rectValid,
  tmatInverse,
  tmatMul,
  tmatPoint,
  tmatRect,
  toFixed8,
  toTwips,
} from '../utils/numeric';
import type { DisplayObjectContainer } from './DisplayObjectContainer';
import type { Stage } from './Stage';
import type { LoaderInfo } from './LoaderInfo';

export const BLEND_MODES = new Set([
  'normal',
  'layer',
  'multiply',
  'screen',
  'lighten',
  'darken',
  'difference',
  'add',
  'subtract',
  'invert',
  'alpha',
  'erase',
  'overlay',
  'hardlight',
  'shader',
]);

/** Change flags of `$dflags` (damage rendering): the object's own state; its child list or frame. */
export const DAMAGE_SELF = 1;
export const DAMAGE_CHILDREN = 2;

/** A device-pixel box [x0, x1) x [y0, y1) (the renderer's Box). */
export interface DamageBox {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

/** Whether two filter lists are equal (same types and parameters): a timeline placing the same filters again. */
function sameFilters(a: readonly BitmapFilter[], b: readonly BitmapFilter[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i].constructor !== b[i].constructor || JSON.stringify(a[i]) !== JSON.stringify(b[i])) return false;
  }
  return true;
}

/** Options for shape hit testing (subset of Ruffle's HitTestOptions). */
export const HIT_SKIP_INVISIBLE = 1;
export const HIT_SKIP_MASK = 2;

/** Result of an AVM2-style mouse pick. */
export type MousePick = { kind: 'hit'; target: any } | { kind: 'propagate' } | { kind: 'miss' };
export const PICK_MISS: MousePick = { kind: 'miss' };
export const PICK_PROPAGATE: MousePick = { kind: 'propagate' };

/**
 * Kind tag consumed by the renderer (see src/flash/display/render-data.ts):
 * 'container' | 'shape' | 'morph' | 'bitmap' | 'text' | 'button' | 'stage' | 'none'.
 */
export type RenderKind = 'container' | 'shape' | 'morph' | 'bitmap' | 'text' | 'button' | 'stage' | 'none';

/**
 * flash.display.DisplayObject. Dynamic: arbitrary properties may be set/read.
 *
 * Internal state is kept in `$`-prefixed fields (never used by the decompiled game code, which
 * does use `_`-prefixed names). Transform storage matches Flash Player: float32 a/b/c/d,
 * integer twips tx/ty, 8.8 fixed alpha/colour multipliers, int16 colour offsets.
 */
export class DisplayObject extends EventDispatcher {
  [key: string]: any;

  /** @internal */ $parent: DisplayObjectContainer | null;
  /** @internal */ $name: string;
  /** @internal */ $m: TMatrix;
  // Cached scale/rotation/skew (Ruffle `cache_scale_rotation`): rotation in degrees.
  /** @internal */ $srValid: boolean;
  /** @internal */ $sx: number;
  /** @internal */ $sy: number;
  /** @internal */ $rot: number;
  /** @internal */ $skew: number;
  // Colour transform: multipliers in 8.8 fixed (256 = 1.0), offsets int16.
  /** @internal */ $ctRM: number;
  /** @internal */ $ctGM: number;
  /** @internal */ $ctBM: number;
  /** @internal */ $ctAM: number;
  /** @internal */ $ctRO: number;
  /** @internal */ $ctGO: number;
  /** @internal */ $ctBO: number;
  /** @internal */ $ctAO: number;
  /** @internal */ $visible: boolean;
  /** @internal */ $z: number;
  /** @internal timeline depth (0 for script-added children). */ $depth: number;
  /** @internal */ $clipDepth: number;
  /** @internal frame at which the timeline placed this object. */ $placeFrame: number;
  /** @internal touched by addChild/removeChild/setChildIndex from script. */ $placedByScript: boolean;
  /** @internal any transform property set by script: timeline stops applying PlaceObject data. */ $transformedByScript: boolean;
  /** @internal created by a timeline PlaceObject. */ $byTimeline: boolean;
  /** @internal name came from the PlaceObject tag. */ $explicitName: boolean;
  /** @internal timeline character id. */ $charId: number;
  /** @internal morph ratio (0..65535). */ $ratio: number;
  /** @internal */ $filters: BitmapFilter[];
  /** @internal */ $blendMode: string;
  /** @internal */ $cacheAsBitmap: boolean;
  /** @internal */ $opaqueBackground: number | null;
  /** @internal */ $scrollRect: Rectangle | null;
  /** @internal */ $scale9Grid: Rectangle | null;
  /** @internal this object's mask */ $mask: DisplayObject | null;
  /** @internal object this object masks */ $maskee: DisplayObject | null;
  /** @internal false while timeline-placed but not yet constructed. */ $constructed: boolean;
  /** @internal symbol context (library + bundle) for objects instantiated from assets. */ $sym: any;
  /** @internal render invalidation counter. */ $version: number;
  /**
   * @internal PORT (wide screens): drawn and hit only inside the stage rectangle, as every object is behind the
   * letterbox of a stage-sized screen. The port sets it on parts of the original screens that lie beyond the stage on
   * purpose (hidden buttons, data fields) while a wide screen shows the margin around the stage (Stage.visibleRect).
   */
  $designClip: boolean;
  /**
   * @internal PORT (damage rendering, src/flash/render/damage.ts): what changed since the last render (DAMAGE_SELF: the
   * object's own state, DAMAGE_CHILDREN: its child list or frame), and the renderer's record of where it was drawn
   * (device box, the record's epoch, the children then).
   */
  $dflags: number;
  /** @internal */ $dbox: DamageBox | null;
  /** @internal */ $dgen: number;
  /** @internal */ $dkids: DisplayObject[] | null;

  constructor() {
    super();
    const r = rt();
    this.$parent = null;
    this.$name = 'instance' + ++r.instanceCounter;
    this.$m = { a: 1, b: 0, c: 0, d: 1, tx: 0, ty: 0 };
    this.$srValid = true;
    this.$sx = 1;
    this.$sy = 1;
    this.$rot = 0;
    this.$skew = 0;
    this.$ctRM = 256;
    this.$ctGM = 256;
    this.$ctBM = 256;
    this.$ctAM = 256;
    this.$ctRO = 0;
    this.$ctGO = 0;
    this.$ctBO = 0;
    this.$ctAO = 0;
    this.$visible = true;
    this.$z = 0;
    this.$depth = 0;
    this.$clipDepth = 0;
    this.$placeFrame = 0;
    this.$placedByScript = false;
    this.$transformedByScript = false;
    this.$byTimeline = false;
    this.$explicitName = false;
    this.$charId = 0;
    this.$ratio = 0;
    this.$filters = [];
    this.$blendMode = 'normal';
    this.$cacheAsBitmap = false;
    this.$opaqueBackground = null;
    this.$scrollRect = null;
    this.$scale9Grid = null;
    this.$mask = null;
    this.$maskee = null;
    this.$constructed = true;
    this.$sym = null;
    this.$version = 0;
    this.$designClip = false;
    this.$dflags = 0;
    this.$dbox = null;
    this.$dgen = -1;
    this.$dkids = null;
  }

  // ------------------------------------------------------------------ kind flags
  /** @internal */ get $isContainer(): boolean {
    return false;
  }
  /** @internal */ get $isInteractive(): boolean {
    return false;
  }
  /** @internal */ get $renderKind(): RenderKind {
    return 'none';
  }

  /** @internal The object's own visual state changed (its transform, colour, content...). */
  $invalidate(): void {
    this.$version++;
    const r = rt();
    r.renderVersion++;
    if (r.changes !== null && !(this.$dflags & DAMAGE_SELF)) {
      if (!this.$dflags) r.changes.push(this);
      this.$dflags |= DAMAGE_SELF;
    }
  }

  /**
   * @internal Only the child list (or a clip's frame) changed: what the children themselves draw is theirs to report
   * (a damage renderer redraws the children added, removed or reordered, not the whole object).
   */
  $invalidateChildren(): void {
    this.$version++;
    const r = rt();
    r.renderVersion++;
    if (r.changes !== null && !(this.$dflags & DAMAGE_CHILDREN)) {
      if (!this.$dflags) r.changes.push(this);
      this.$dflags |= DAMAGE_CHILDREN;
    }
  }

  // ------------------------------------------------------------------ position / transform
  get x(): number {
    return this.$m.tx / TWIPS;
  }
  set x(v: number) {
    const t = toTwips(+v);
    this.$transformedByScript = true;
    // (a value set again changes nothing on screen: no redraw)
    if (this.$m.tx === t) return;
    this.$m.tx = t;
    this.$invalidate();
  }
  get y(): number {
    return this.$m.ty / TWIPS;
  }
  set y(v: number) {
    const t = toTwips(+v);
    this.$transformedByScript = true;
    if (this.$m.ty === t) return;
    this.$m.ty = t;
    this.$invalidate();
  }
  get z(): number {
    return this.$z;
  }
  set z(v: number) {
    this.$z = +v;
    this.$transformedByScript = true;
  }

  /** @internal */ $cacheScaleRotation(): void {
    if (this.$srValid) return;
    const { a, b, c, d } = this.$m;
    const rotX = Math.atan2(b, a);
    const rotY = Math.atan2(-c, d);
    this.$rot = (rotX * 180) / Math.PI;
    this.$sx = Math.sqrt(a * a + b * b);
    this.$sy = Math.sqrt(c * c + d * d);
    this.$skew = rotY - rotX;
    this.$srValid = true;
  }

  get rotation(): number {
    this.$cacheScaleRotation();
    const rem = this.$rot % 360;
    return rem <= 180 ? rem : rem - 360;
  }
  set rotation(v: number) {
    let deg = +v % 360;
    if (deg < -180) deg += 360;
    else if (deg > 180) deg -= 360;
    this.$transformedByScript = true;
    this.$cacheScaleRotation();
    this.$rot = deg;
    if (deg !== deg) {
      this.$invalidate();
      return;
    }
    const rad = (deg * Math.PI) / 180;
    const skew = this.$skew;
    const m = this.$m;
    const a = fround(this.$sx * Math.cos(rad));
    const b = fround(this.$sx * Math.sin(rad));
    const c = fround(this.$sy * -Math.sin(rad + skew));
    const d = fround(this.$sy * Math.cos(rad + skew));
    if (m.a === a && m.b === b && m.c === c && m.d === d) return;
    m.a = a;
    m.b = b;
    m.c = c;
    m.d = d;
    this.$invalidate();
  }

  get scaleX(): number {
    this.$cacheScaleRotation();
    return this.$sx;
  }
  set scaleX(v: number) {
    v = +v;
    this.$transformedByScript = true;
    this.$cacheScaleRotation();
    this.$sx = v;
    if (v !== v) v = 0;
    let rot = (this.$rot * Math.PI) / 180;
    if (rot !== rot) rot = 0;
    const a = fround(Math.cos(rot) * v);
    const b = fround(Math.sin(rot) * v);
    if (this.$m.a === a && this.$m.b === b) return;
    this.$m.a = a;
    this.$m.b = b;
    this.$invalidate();
  }
  get scaleY(): number {
    this.$cacheScaleRotation();
    return this.$sy;
  }
  set scaleY(v: number) {
    v = +v;
    this.$transformedByScript = true;
    this.$cacheScaleRotation();
    this.$sy = v;
    if (v !== v) v = 0;
    let rot = (this.$rot * Math.PI) / 180;
    if (rot !== rot) rot = 0;
    const ang = rot + this.$skew;
    const c = fround(-Math.sin(ang) * v);
    const d = fround(Math.cos(ang) * v);
    if (this.$m.c === c && this.$m.d === d) return;
    this.$m.c = c;
    this.$m.d = d;
    this.$invalidate();
  }
  get scaleZ(): number {
    return 1;
  }
  set scaleZ(_v: number) {
    /* 3D not supported */
  }
  get rotationX(): number {
    return 0;
  }
  set rotationX(_v: number) {}
  get rotationY(): number {
    return 0;
  }
  set rotationY(_v: number) {}
  get rotationZ(): number {
    return this.rotation;
  }
  set rotationZ(v: number) {
    this.rotation = v;
  }

  /** @internal set the whole matrix (timeline / transform.matrix). */
  $setMatrix(m: TMatrix): void {
    this.$srValid = false;
    if (!this.$setMatrixQuiet(m)) return;
    this.$invalidate();
  }

  /** @internal Sets the matrix without invalidating; whether it changed. */
  $setMatrixQuiet(m: TMatrix): boolean {
    const a = fround(m.a);
    const b = fround(m.b);
    const c = fround(m.c);
    const d = fround(m.d);
    const tx = m.tx | 0;
    const ty = m.ty | 0;
    const o = this.$m;
    if (o.a === a && o.b === b && o.c === c && o.d === d && o.tx === tx && o.ty === ty) return false;
    this.$m = { a, b, c, d, tx, ty };
    return true;
  }

  get width(): number {
    const r = this.$boundsWithTransform(this.$m);
    return rectValid(r) ? (r.xMax - r.xMin) / TWIPS : 0;
  }
  set width(value: number) {
    value = +value;
    if (!(value >= 0)) return;
    this.$setScaledSize(value, true);
  }
  get height(): number {
    const r = this.$boundsWithTransform(this.$m);
    return rectValid(r) ? (r.yMax - r.yMin) / TWIPS : 0;
  }
  set height(value: number) {
    value = +value;
    if (!(value >= 0)) return;
    this.$setScaledSize(value, false);
  }

  /** @internal Ruffle's width/height setter algorithm (derived empirically from Flash). */
  $setScaledSize(value: number, isWidth: boolean): void {
    const b = this.$boundsWithTransform(IDENTITY);
    const objW = rectValid(b) ? (b.xMax - b.xMin) / TWIPS : 0;
    const objH = rectValid(b) ? (b.yMax - b.yMin) / TWIPS : 0;
    const prevSX = this.scaleX;
    const prevSY = this.scaleY;
    this.$cacheScaleRotation();
    const rad = (this.$rot * Math.PI) / 180;
    const cos = Math.abs(Math.cos(rad));
    const sin = Math.abs(Math.sin(rad));
    let newSX: number;
    let newSY: number;
    if (isWidth) {
      const aspect = objH / objW;
      const tsx = objW !== 0 ? value / objW : 0;
      const tsy = objW !== 0 ? value / objH : 0;
      newSX = (aspect * (cos * tsx + sin * tsy)) / ((cos + aspect * sin) * (aspect * cos + sin));
      newSY = (sin * prevSX + aspect * cos * prevSY) / (aspect * cos + sin);
    } else {
      const aspect = objW / objH;
      const tsx = objH !== 0 ? value / objW : 0;
      const tsy = objH !== 0 ? value / objH : 0;
      newSX = (aspect * cos * prevSX + sin * prevSY) / (aspect * cos + sin);
      newSY = (aspect * (sin * tsx + cos * tsy)) / ((cos + aspect * sin) * (aspect * cos + sin));
    }
    if (!Number.isFinite(newSX)) newSX = 0;
    if (!Number.isFinite(newSY)) newSY = 0;
    this.scaleX = newSX;
    this.scaleY = newSY;
  }

  get alpha(): number {
    return this.$ctAM / 256;
  }
  set alpha(v: number) {
    this.$transformedByScript = true;
    const am = toFixed8(+v);
    if (this.$ctAM === am) return;
    this.$ctAM = am;
    this.$invalidate();
  }

  get visible(): boolean {
    return this.$visible;
  }
  set visible(v: boolean) {
    if (this.$visible === !!v) return;
    this.$visible = !!v;
    this.$invalidate();
  }

  get name(): string {
    return this.$name;
  }
  set name(v: string) {
    if (this.$byTimeline) throw new Error('Error #2078: The name property of a Timeline-placed object cannot be modified.');
    this.$name = v === null || v === undefined ? (null as any) : String(v);
  }

  get parent(): DisplayObjectContainer | null {
    return this.$parent;
  }

  get stage(): Stage | null {
    let o: any = this;
    while (o) {
      if (o.$isStage) return o;
      o = o.$parent;
    }
    return null;
  }

  get root(): DisplayObject | null {
    let o: any = this;
    if (o.$isStage) return o;
    while (o.$parent) {
      if (o.$parent.$isStage) return o;
      o = o.$parent;
    }
    return o.$isRootCandidate ? o : null;
  }

  get loaderInfo(): LoaderInfo | null {
    const s: any = this.stage;
    if (s && (this.root === this || (this as any).$isStage)) return s.$loaderInfo ?? null;
    return (this as any).$loaderInfo ?? null;
  }

  get mask(): DisplayObject | null {
    return this.$mask;
  }
  set mask(m: DisplayObject | null) {
    if (this.$mask === (m ?? null) && (!m || m.$maskee === this)) return;
    if (this.$mask) this.$mask.$maskee = null;
    if (m) {
      if (m.$maskee && m.$maskee !== this) m.$maskee.$mask = null;
      m.$maskee = this;
    }
    this.$mask = m ?? null;
    this.$invalidate();
  }

  get filters(): any[] {
    return this.$filters.map((f) => f.clone());
  }
  set filters(v: any[]) {
    const out: BitmapFilter[] = [];
    if (v) {
      for (const f of v) {
        if (f instanceof BitmapFilter) out.push(f.clone());
        else throw new TypeError('Error #2005: Parameter 0 is of the incorrect type. Should be type Filter.');
      }
    }
    if (!out.length && !this.$filters.length) return;
    this.$filters = out;
    this.$invalidate();
  }

  get blendMode(): string {
    return this.$blendMode;
  }
  set blendMode(v: string) {
    v = String(v);
    if (!BLEND_MODES.has(v)) throw new Error('ArgumentError: Error #2008: Parameter blendMode must be one of the accepted values.');
    if (this.$blendMode === v) return;
    this.$blendMode = v;
    this.$invalidate();
  }

  get cacheAsBitmap(): boolean {
    return this.$cacheAsBitmap || this.$filters.length > 0;
  }
  set cacheAsBitmap(v: boolean) {
    this.$cacheAsBitmap = !!v;
  }

  get opaqueBackground(): any {
    return this.$opaqueBackground;
  }
  set opaqueBackground(v: any) {
    const ob = v === null || v === undefined ? null : Number(v) >>> 0 & 0xffffff;
    if (this.$opaqueBackground === ob) return;
    this.$opaqueBackground = ob;
    this.$invalidate();
  }

  get scrollRect(): Rectangle | null {
    return this.$scrollRect ? this.$scrollRect.clone() : null;
  }
  set scrollRect(r: Rectangle | null) {
    const o = this.$scrollRect;
    if (!r && !o) return;
    if (r && o && r.x === o.x && r.y === o.y && r.width === o.width && r.height === o.height) return;
    this.$scrollRect = r ? r.clone() : null;
    this.$invalidate();
  }

  get scale9Grid(): Rectangle | null {
    return this.$scale9Grid ? this.$scale9Grid.clone() : null;
  }
  set scale9Grid(r: Rectangle | null) {
    this.$scale9Grid = r ? r.clone() : null;
    this.$invalidate();
  }

  get transform(): Transform {
    return new Transform(this);
  }
  set transform(t: Transform) {
    if (!t) throw new TypeError('Error #2007: Parameter transform must be non-null.');
    this.$setMatrix(t.$matrixTwips());
    this.$setColorTransformFrom(t.colorTransform);
    this.$transformedByScript = true;
  }

  /** @internal */ $setColorTransformFrom(ct: {
    redMultiplier: number;
    greenMultiplier: number;
    blueMultiplier: number;
    alphaMultiplier: number;
    redOffset: number;
    greenOffset: number;
    blueOffset: number;
    alphaOffset: number;
  }): void {
    if (!this.$setColorQuiet(ct.redMultiplier, ct.greenMultiplier, ct.blueMultiplier, ct.alphaMultiplier, ct.redOffset, ct.greenOffset, ct.blueOffset, ct.alphaOffset)) return;
    this.$invalidate();
  }

  /** @internal Sets the colour transform without invalidating; whether it changed. */
  $setColorQuiet(rm: number, gm: number, bm: number, am: number, ro: number, go: number, bo: number, ao: number): boolean {
    const RM = toFixed8(rm);
    const GM = toFixed8(gm);
    const BM = toFixed8(bm);
    const AM = toFixed8(am);
    const RO = toI16Wrap(ro);
    const GO = toI16Wrap(go);
    const BO = toI16Wrap(bo);
    const AO = toI16Wrap(ao);
    if (this.$ctRM === RM && this.$ctGM === GM && this.$ctBM === BM && this.$ctAM === AM && this.$ctRO === RO && this.$ctGO === GO && this.$ctBO === BO && this.$ctAO === AO) return false;
    this.$ctRM = RM;
    this.$ctGM = GM;
    this.$ctBM = BM;
    this.$ctAM = AM;
    this.$ctRO = RO;
    this.$ctGO = GO;
    this.$ctBO = BO;
    this.$ctAO = AO;
    return true;
  }

  /** @internal colour transform as [rm, gm, bm, am, ro, go, bo, ao] (multipliers as reals). */
  $colorTransformArray(): [number, number, number, number, number, number, number, number] {
    return [this.$ctRM / 256, this.$ctGM / 256, this.$ctBM / 256, this.$ctAM / 256, this.$ctRO, this.$ctGO, this.$ctBO, this.$ctAO];
  }

  get mouseX(): number {
    const p = this.$globalMouseLocal();
    return p ? p[0] / TWIPS : 0;
  }
  get mouseY(): number {
    const p = this.$globalMouseLocal();
    return p ? p[1] / TWIPS : 0;
  }
  /** @internal */ $globalMouseLocal(): [number, number] | null {
    const player = rt().player;
    const gx = player ? player.$mouseTwipsX : 0;
    const gy = player ? player.$mouseTwipsY : 0;
    const inv = tmatInverse(this.$localToGlobalMatrix());
    if (!inv) return null;
    return tmatPoint(inv, gx, gy);
  }

  get accessibilityProperties(): any {
    return null;
  }
  set accessibilityProperties(_v: any) {}

  // ------------------------------------------------------------------ matrices & bounds
  /** @internal Local->global (stage) matrix including ancestors' scrollRect offsets. */
  $localToGlobalMatrix(): TMatrix {
    let m: TMatrix = { ...this.$m };
    let p: any = this.$parent;
    while (p) {
      if (p.$scrollRect) {
        m = tmatMul({ a: 1, b: 0, c: 0, d: 1, tx: -toTwips(p.$scrollRect.x), ty: -toTwips(p.$scrollRect.y) }, m);
      }
      m = tmatMul(p.$m, m);
      p = p.$parent;
    }
    return m;
  }

  /** @internal Bounds of this object's own content (no children), local space, twips. */
  $selfBounds(): TRect {
    return invalidRect();
  }

  /** @internal Bounds of self + children transformed by `m` (Ruffle bounds_with_transform). */
  $boundsWithTransform(m: TMatrix): TRect {
    const sr = this.$scrollRect;
    if (sr) {
      return tmatRect(m, { xMin: 0, yMin: 0, xMax: toTwips(sr.width), yMax: toTwips(sr.height) });
    }
    let r = tmatRect(m, this.$selfBounds());
    const kids = this.$boundsChildren();
    if (kids) {
      for (let i = 0; i < kids.length; i++) {
        const c = kids[i];
        r = rectUnion(r, c.$boundsWithTransform(tmatMul(m, c.$m)));
      }
    }
    return r;
  }

  /** @internal Children that contribute to bounds (containers: render list; buttons: current state). */
  $boundsChildren(): DisplayObject[] | null {
    return null;
  }

  /** @internal World (stage-space) bounds. */
  $worldBounds(): TRect {
    return this.$boundsWithTransform(this.$localToGlobalMatrix());
  }

  getBounds(targetCoordinateSpace: DisplayObject | null): Rectangle {
    const target = targetCoordinateSpace ?? this;
    let r: TRect;
    if (target === this) r = this.$boundsWithTransform(IDENTITY);
    else {
      const toGlobal = this.$localToGlobalMatrix();
      const toTarget = tmatInverse(target.$localToGlobalMatrix()) ?? { ...IDENTITY };
      r = this.$boundsWithTransform(tmatMul(toTarget, toGlobal));
    }
    if (!rectValid(r)) return new Rectangle(0, 0, 0, 0);
    return new Rectangle(r.xMin / TWIPS, r.yMin / TWIPS, (r.xMax - r.xMin) / TWIPS, (r.yMax - r.yMin) / TWIPS);
  }

  getRect(targetCoordinateSpace: DisplayObject | null): Rectangle {
    // Strokes are not modelled separately; getRect == getBounds.
    return this.getBounds(targetCoordinateSpace);
  }

  localToGlobal(p: Point): Point {
    const [x, y] = tmatPoint(this.$localToGlobalMatrix(), toTwips(p.x), toTwips(p.y));
    return new Point(x / TWIPS, y / TWIPS);
  }

  globalToLocal(p: Point): Point {
    const inv = tmatInverse(this.$localToGlobalMatrix());
    if (!inv) return new Point(0, 0);
    const [x, y] = tmatPoint(inv, toTwips(p.x), toTwips(p.y));
    return new Point(x / TWIPS, y / TWIPS);
  }

  hitTestPoint(x: number, y: number, shapeFlag: boolean = false): boolean {
    const tx = toTwips(+x);
    const ty = toTwips(+y);
    if (shapeFlag) return this.$hitTestShape(tx, ty, HIT_SKIP_MASK);
    const b = this.$worldBounds();
    return rectValid(b) && rectContains(b, tx, ty);
  }

  hitTestObject(obj: DisplayObject): boolean {
    const a = this.$worldBounds();
    const b = obj.$worldBounds();
    if (!rectValid(a) || !rectValid(b)) return false;
    return a.xMin <= b.xMax && a.xMax >= b.xMin && a.yMin <= b.yMax && a.yMax >= b.yMin;
  }

  /**
   * @internal Shape-accurate hit test at a global (stage, twips) point. Default: world bounds.
   * Subclasses refine (Shape: image alpha, containers: children + clip layers).
   */
  $hitTestShape(gx: number, gy: number, options: number): boolean {
    if (options & HIT_SKIP_INVISIBLE && !this.$visible && !this.$maskee) return false;
    if (options & HIT_SKIP_MASK && this.$maskee) return false;
    const b = this.$worldBounds();
    if (!rectValid(b) || !rectContains(b, gx, gy)) return false;
    if (this.$mask && !this.$mask.$hitTestShape(gx, gy, HIT_SKIP_INVISIBLE)) return false;
    return this.$hitTestLocalContent(gx, gy);
  }

  /** @internal Content test once bounds/mask passed; default true (bounds-accurate). */
  $hitTestLocalContent(_gx: number, _gy: number): boolean {
    return true;
  }

  /** @internal Global twips point -> local pixel coordinates (double precision). */
  $globalToLocalPx(gx: number, gy: number): [number, number] | null {
    const m = this.$localToGlobalMatrix();
    const det = m.a * m.d - m.b * m.c;
    if (det === 0 || !Number.isFinite(det)) return null;
    const px = gx / TWIPS - m.tx / TWIPS;
    const py = gy / TWIPS - m.ty / TWIPS;
    return [(m.d * px - m.c * py) / det, (-m.b * px + m.a * py) / det];
  }

  /** @internal AVM2 mouse pick (non-interactive objects never pick themselves). */
  $mousePick(_gx: number, _gy: number): MousePick {
    return PICK_MISS;
  }

  // ------------------------------------------------------------------ frame lifecycle hooks
  /** @internal Enter phase (timeline advance). Containers recurse. */
  $enterFrame(): void {}
  /** @internal Construct phase for this subtree. */
  $constructFrame(): void {}
  /** @internal Frame-scripts traversal (MovieClip overrides). */
  $runFrameScripts(): void {}

  /** @internal Called by the timeline when (re)placing: apply PlaceObject properties. */
  $applyPlace(p: {
    matrix?: number[];
    colorTransform?: number[];
    ratio?: number;
    filters?: BitmapFilter[];
    blendMode?: string;
    visible?: boolean;
    cacheAsBitmap?: boolean;
  }): void {
    if (this.$transformedByScript) return;
    // PORT: a timeline re-applies each frame's placement to its children; only a placement that changes something is
    // a change on screen (a looping clip whose children keep their place is not redrawn for them).
    let changed = false;
    if (p.matrix) {
      const [a, b, c, d, tx, ty] = p.matrix;
      this.$srValid = false;
      if (this.$setMatrixQuiet({ a, b, c, d, tx: Math.round(tx * TWIPS), ty: Math.round(ty * TWIPS) })) changed = true;
    }
    if (p.colorTransform) {
      const [rm, gm, bm, am, ro, go, bo, ao] = p.colorTransform;
      if (this.$setColorQuiet(rm, gm, bm, am, ro, go, bo, ao)) changed = true;
    }
    if (p.ratio !== undefined) this.$setRatio(p.ratio);
    if (p.cacheAsBitmap !== undefined) this.$cacheAsBitmap = p.cacheAsBitmap;
    if (p.blendMode !== undefined && BLEND_MODES.has(p.blendMode) && this.$blendMode !== p.blendMode) {
      this.$blendMode = p.blendMode;
      changed = true;
    }
    if (p.visible !== undefined && this.$visible !== p.visible) {
      this.$visible = p.visible;
      changed = true;
    }
    if (p.filters !== undefined && !sameFilters(this.$filters, p.filters)) {
      this.$filters = p.filters.map((f) => f.clone());
      changed = true;
    }
    if (changed) this.$invalidate();
  }

  /** @internal */ $setRatio(r: number): void {
    this.$ratio = r;
  }

  /** @internal Replace the character (PlaceObject "replace"); only graphics support it. */
  $replaceCharacter(_charId: number): void {}

  /** @internal Dispatch a non-bubbling event to this object and all descendants (pre-order). */
  $dispatchRecursive(type: string): void {
    const snapshot: DisplayObject[] = [];
    collectPreOrder(this, snapshot);
    for (const o of snapshot) o.$dispatch(new Event(type, false, false));
  }

  /** @internal enterFrame etc. are delivered to all display objects (on or off the list). */
  $acceptsBroadcast(_type: string): boolean {
    return true;
  }

  /** @internal all state children for lifecycle traversal. */
  $lifecycleChildren(): DisplayObject[] | null {
    return null;
  }

  toString(): string {
    return `[object ${(this.constructor as any).name || 'DisplayObject'}]`;
  }
}

/** Wrap to int16 like ECMAScript ToInt16 of a truncated value (colour offsets). */
export function toI16Wrap(v: number): number {
  if (!Number.isFinite(v)) return 0;
  let t = Math.trunc(v) % 65536;
  if (t < 0) t += 65536;
  return t >= 32768 ? t - 65536 : t;
}

/** @internal Pre-order collection over containers and button states. */
export function collectPreOrder(o: DisplayObject, out: DisplayObject[]): void {
  out.push(o);
  const kids = o.$lifecycleChildren();
  if (kids) for (const k of kids) collectPreOrder(k, out);
}
